import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MessageSquare, RefreshCw, Download, Search, X,
  Star, ThumbsUp, ThumbsDown, Minus, Loader2,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Eye,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import apiClient from '../../services/api/client';
import { ENDPOINTS } from '../../services/api/endpoints';
import { useSeller } from '../../context/SellerContext';
import MarketplaceSelector from '../../components/shared/MarketplaceSelector';

const PAGE_SIZES = [25, 50, 100];

const CLASSIFICATION_CONFIG = {
  POSITIVE: { label: 'Positive', cls: 'bg-emerald-100 text-emerald-700' },
  NEUTRAL: { label: 'Neutral', cls: 'bg-amber-100 text-amber-700' },
  NEGATIVE: { label: 'Negative', cls: 'bg-red-100 text-red-700' },
};

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diffMs = new Date() - new Date(dateStr);
  if (diffMs < 0) return '';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={13} className={i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />
      ))}
    </div>
  );
}

function exportToCsv(data) {
  const headers = ['Order ID', 'Date', 'Rating', 'Classification', 'Comments', 'Rater Email', 'Response', 'Seller'];
  const rows = data.map(f => [
    f.amazonOrderId || '', f.feedbackDate || '', f.rating,
    f.classification || '', `"${(f.comments || '').replace(/"/g, '""')}"`,
    f.raterEmail || '', `"${(f.response || '').replace(/"/g, '""')}"`,
    f.sellerName || '',
  ]);
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `feedbacks-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Detail Panel ────────────────────────────────────────────────────────────

function FeedbackDetailPanel({ feedback, isOpen, onClose }) {
  if (!isOpen || !feedback) return null;
  const cc = CLASSIFICATION_CONFIG[feedback.classification] || CLASSIFICATION_CONFIG.NEUTRAL;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 z-10 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Feedback Details</h3>
            <p className="text-xs text-gray-400 mt-0.5">{feedback.amazonOrderId}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X size={20} /></button>
        </div>
        <div className="p-3 sm:p-5 space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <StarRating rating={feedback.rating} />
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${cc.cls}`}>{cc.label}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-gray-500">Order ID</p><p className="text-gray-800 font-mono">{feedback.amazonOrderId || '-'}</p></div>
              <div><p className="text-gray-500">Date</p><p className="text-gray-800">{formatDate(feedback.feedbackDate)}</p></div>
              <div><p className="text-gray-500">Seller</p><p className="text-gray-800">{feedback.sellerName || '-'}</p></div>
              <div><p className="text-gray-500">Rating</p><p className="text-gray-800 font-bold">{feedback.rating}/5</p></div>
            </div>
          </div>
          {feedback.comments && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Buyer Comments</p>
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{feedback.comments}</p>
            </div>
          )}
          {feedback.response && (
            <div className="bg-[#FFF4E6] rounded-lg p-4">
              <p className="text-xs font-semibold text-[#C45500] uppercase tracking-wider mb-2">Seller Response</p>
              <p className="text-sm text-[#C45500] leading-relaxed whitespace-pre-wrap">{feedback.response}</p>
            </div>
          )}
          {feedback.raterEmail && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Rater Email</p>
              <p className="text-sm text-gray-700">{feedback.raterEmail}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════

export default function ReviewsFeedbackPage() {
  const { selectedSeller: sellerCtx } = useSeller();
  const sellerId = sellerCtx?.id || '';
  const sellerChannels = useMemo(() => (sellerCtx?.channels || []).filter(c => c.accountStatus === 'AUTHORIZED'), [sellerCtx]);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  const [pageData, setPageData] = useState(null);
  const [filterClassification, setFilterClassification] = useState('');
  const [filterRating, setFilterRating] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [sortBy, setSortBy] = useState('feedbackDate');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncDays, setSyncDays] = useState('365');
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sellerId) params.append('sellerId', sellerId);
      if (filterClassification) params.append('classification', filterClassification);
      if (filterRating) params.append('rating', filterRating);
      if (search) params.append('search', search);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      params.append('page', String(page));
      params.append('size', String(size));
      const res = await apiClient.get(`${ENDPOINTS.SELLER_FEEDBACKS.GET_ALL}?${params}`);
      setPageData(res.data || res);
    } catch {
      toast.error('Failed to load feedbacks');
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [sellerId, filterClassification, filterRating, search, dateFrom, dateTo, page, size]);

  useEffect(() => { fetchFeedbacks(); }, [fetchFeedbacks]);

  const handleSync = async () => {
    const filtered = sellerChannels.filter(a => a.platform === 'AMAZON');
    if (filtered.length === 0) { toast.error('No authorized Amazon accounts'); return; }
    setSyncing(true);
    let totalNew = 0;
    for (const account of filtered) {
      try {
        toast(`Syncing feedback for ${account.sellerName || account.id}...`, { icon: '🔍', duration: 2000 });
        const res = await apiClient.post(`${ENDPOINTS.SELLER_FEEDBACKS.SYNC}?sellerAccountId=${account.id}&daysBack=${syncDays}`);
        const data = res.data || res;
        totalNew += data.newFeedbackCount || 0;
      } catch (err) {
        toast.error(`Failed: ${account.sellerName || account.id}`);
      }
    }
    if (totalNew > 0) toast.success(`${totalNew} new feedback${totalNew > 1 ? 's' : ''} synced`);
    else toast('No new feedback found', { icon: 'ℹ️' });
    await fetchFeedbacks();
    setSyncing(false);
  };

  const rawContent = pageData?.content;
  const feedbacks = Array.isArray(rawContent) ? rawContent : [];
  const totalElements = pageData?.totalElements || 0;
  const totalPages = Math.ceil(totalElements / size) || 0;
  const stats = pageData?.stats || { total: 0, positive: 0, neutral: 0, negative: 0, avg: 0 };

  const startRecord = totalElements > 0 ? page * size + 1 : 0;
  const endRecord = Math.min((page + 1) * size, totalElements);

  const hasFilters = filterClassification || filterRating || search || dateFrom || dateTo;
  const clearFilters = () => { setFilterClassification(''); setFilterRating(''); setSearch(''); setDateFrom(''); setDateTo(''); setPage(0); };

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ChevronDown size={14} className="text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp size={14} className="text-[#C45500]" /> : <ChevronDown size={14} className="text-[#C45500]" />;
  };

  // Client-side sort (since API doesn't support sort params yet)
  const sorted = useMemo(() => {
    return [...feedbacks].sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (va == null) return 1; if (vb == null) return -1;
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [feedbacks, sortBy, sortDir]);

  const selectCls = "px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]";

  return (
    <AdminLayout>
      <div className="p-3 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Reviews & Feedback</h1>
            <p className="text-xs text-slate-400 mt-0.5">Customer product reviews and seller feedback from Amazon</p>
          </div>
          <div className="flex items-center gap-2">
            <MarketplaceSelector value={selectedAccountId} onChange={setSelectedAccountId} />

            <div className="w-px h-8 bg-gray-200" />

            <button onClick={fetchFeedbacks} disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50" title="Refresh">
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>

            <div className="w-px h-8 bg-gray-200" />

            <select value={syncDays} onChange={(e) => setSyncDays(e.target.value)} className={selectCls}>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="180">Last 6 months</option>
              <option value="365">Last 1 year</option>
            </select>
            <button onClick={handleSync} disabled={syncing || !sellerId}
              title={!sellerId ? 'Select a seller first' : ''}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#232F3E] text-white rounded-lg text-sm font-semibold hover:bg-[#37475A] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">
              {syncing ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              {syncing ? 'Syncing...' : 'Sync Feedback'}
            </button>
          </div>
        </div>

        {/* Stats — not affected by row filters */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Feedback', value: stats.total, icon: MessageSquare, color: 'bg-[#FFF4E6]0' },
            { label: 'Positive', value: stats.positive, icon: ThumbsUp, color: 'bg-emerald-500' },
            { label: 'Neutral', value: stats.neutral, icon: Minus, color: 'bg-amber-500' },
            { label: 'Negative', value: stats.negative, icon: ThumbsDown, color: 'bg-rose-500' },
            { label: 'Avg Rating', value: stats.avg ? `${stats.avg}/5` : '-', icon: Star, color: 'bg-[#37475A]' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{s.label}</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{s.value}</p>
                </div>
                <div className={`${s.color} p-3 rounded-lg`}><s.icon size={20} className="text-white" /></div>
              </div>
            </div>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search order ID, comments..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
              {search && <button onClick={() => { setSearch(''); setPage(0); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"><X size={14} /></button>}
            </div>
            <select value={filterClassification} onChange={(e) => { setFilterClassification(e.target.value); setPage(0); }} className={selectCls}>
              <option value="">All Ratings</option>
              <option value="POSITIVE">Positive (4-5)</option>
              <option value="NEUTRAL">Neutral (3)</option>
              <option value="NEGATIVE">Negative (1-2)</option>
            </select>
            <select value={filterRating} onChange={(e) => { setFilterRating(e.target.value); setPage(0); }} className={selectCls}>
              <option value="">All Stars</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0); }} className={`${selectCls} w-[140px]`} title="From date" />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0); }} className={`${selectCls} w-[140px]`} title="To date" />
            {hasFilters && (
              <button onClick={clearFilters} className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-800">
                <X size={14} className="mr-1" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Results Row */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {totalElements > 0 ? `Showing ${startRecord}-${endRecord} of ${totalElements} feedbacks` : 'No feedbacks found'}
          </span>
          <div className="flex items-center gap-3">
            <button onClick={() => exportToCsv(sorted)} disabled={sorted.length === 0}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40">
              <Download size={14} className="mr-1.5" /> Export CSV
            </button>
            <select value={size} onChange={(e) => { setSize(parseInt(e.target.value)); setPage(0); }}
              className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20">
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none" onClick={() => handleSort('amazonOrderId')}>
                    <div className="flex items-center gap-1">Order ID <SortIcon field="amazonOrderId" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none" onClick={() => handleSort('feedbackDate')}>
                    <div className="flex items-center gap-1">Date <SortIcon field="feedbackDate" /></div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer select-none" onClick={() => handleSort('rating')}>
                    <div className="flex items-center justify-center gap-1">Rating <SortIcon field="rating" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none" onClick={() => handleSort('classification')}>
                    <div className="flex items-center gap-1">Type <SortIcon field="classification" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comments</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seller</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>{Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-4 py-4"><div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${50 + Math.random() * 50}%` }} /></td>
                    ))}</tr>
                  ))
                ) : sorted.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center">
                      <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500 text-lg font-medium">No feedbacks found</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {hasFilters ? 'Try adjusting your filters.' : 'Select a seller and click "Sync Feedback" to fetch from Amazon.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  sorted.map(f => {
                    const cc = CLASSIFICATION_CONFIG[f.classification] || CLASSIFICATION_CONFIG.NEUTRAL;
                    return (
                      <tr key={f.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedFeedback(f)}>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-[#C45500] font-mono">{f.amazonOrderId || '-'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-700">{formatDate(f.feedbackDate)}</p>
                          <p className="text-xs text-gray-400">{timeAgo(f.feedbackDate)}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <StarRating rating={f.rating} />
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${cc.cls}`}>{cc.label}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-600 truncate max-w-[250px]" title={f.comments}>{f.comments || '-'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs text-gray-500">{f.sellerName || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={(e) => { e.stopPropagation(); setSelectedFeedback(f); }}
                            className="text-gray-400 hover:text-[#C45500] p-1" title="View details">
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50">
              <span className="text-sm text-gray-500">Page {page + 1} of {totalPages}</span>
              <div className="flex items-center space-x-1">
                <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                  className="inline-flex items-center px-3 py-1.5 rounded text-sm text-gray-600 hover:bg-white hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  <ChevronLeft size={16} className="mr-1" /> Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pn;
                  if (totalPages <= 5) pn = i;
                  else if (page < 3) pn = i;
                  else if (page > totalPages - 4) pn = totalPages - 5 + i;
                  else pn = page - 2 + i;
                  return (
                    <button key={pn} onClick={() => setPage(pn)}
                      className={`px-3 py-1.5 rounded text-sm ${page === pn ? 'bg-[#232F3E] text-white' : 'text-gray-600 hover:bg-white hover:shadow-sm'}`}>
                      {pn + 1}
                    </button>
                  );
                })}
                <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
                  className="inline-flex items-center px-3 py-1.5 rounded text-sm text-gray-600 hover:bg-white hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  Next <ChevronRight size={16} className="ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <FeedbackDetailPanel feedback={selectedFeedback} isOpen={!!selectedFeedback}
        onClose={() => setSelectedFeedback(null)} />
    </AdminLayout>
  );
}
